import { useCallback, useEffect, useState } from "react";
import Input from "../input/Input";
import styles from "./ResetPasswordForm.module.scss";
import Button from "../button/Button";
import SendCodeModal from "../modals/SendCodeModal/SendCodeModal";
import UpdatePasswordModal from "../modals/UpdatePasswordModal/UpdatePasswordModal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForgotPasswordMutation } from "../../store/services/auth-api";
import arrow from "../../assets/icons/arrow-right.svg";
import { trackEvent } from "../../utils/analytics";
import { EVENT_NAMES } from "../../types/event-names";

interface ResetPasswordFormProps {
  email?: string;
  isForgotPasswordPage?: boolean;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ email = "", isForgotPasswordPage = false }) => {
  const [emailFocused, setEmailFocused] = useState(false);
  const [emailValue, setEmailValue] = useState(email || "");
  const [emailError, setEmailError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [forgotPassword] = useForgotPasswordMutation();

  const handleSendEmail = useCallback(async () => {
    if (!emailValue || emailError) return;

    try {
      setIsLoading(true);
      sessionStorage.setItem("email", emailValue);
      trackEvent(EVENT_NAMES.FORGOT_PASSWORD_EMAIL_SUBMITTED, {
        email: emailValue,
      });
      await forgotPassword({ email: emailValue }).unwrap();

      trackEvent(EVENT_NAMES.FORGOT_PASSWORD_VERIFICATION_SENT);

      setIsModalVisible(true);
    } catch (error) {
      setEmailError(true);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [emailValue, emailError, forgotPassword, setIsModalVisible]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        handleSendEmail();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleSendEmail]);

  const handleCodeSubmit = async (code: string) => {
    navigate(`/create-new-password/${code}`);
  };

  const goBack = () => {
    navigate("/");
  };

  useEffect(() => {
    trackEvent(EVENT_NAMES.FORGOT_PASSWORD_OPENED);
  }, []);

  return (
    <>
      {!isChangePasswordModalVisible && !isModalVisible ? (
        <div className={styles.root}>
          <p className={styles.root_back} onClick={goBack}>
            <img src={arrow} className={styles.root_icon} alt="back" />
            {t("Back")}
          </p>
          <p className={styles.root_title}>{t("Forgot your password?")}</p>
          <p className={styles.root_subtitle}>{t("Enter your email address to reset it")}</p>
          <div>
            <Input
              value={emailValue}
              type="text"
              onFocus={() => setEmailFocused(true)}
              onBlur={() => {
                setEmailFocused(false);
                const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailPattern.test(emailValue)) {
                  setEmailError(true);
                } else {
                  setEmailError(false);
                }
              }}
              error={emailError}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setEmailError(false);
              }}
              focused={emailFocused}
              placeholder={t("Email address")}
            />
            {emailError && (
              <p className={styles.root_error}>{t("Email not found - please double-check and try again.")}</p>
            )}
            <Button
              text={t("Reset password")}
              onClick={() => {
                if (emailValue) {
                  !emailError && emailValue && handleSendEmail();
                } else {
                  setEmailError(true);
                }
              }}
              classNames={styles.root_button}
              isLoader={isLoading}
            />
          </div>
          <a
            onClick={() => {
              trackEvent(EVENT_NAMES.CONTACT_SUPPORT, {
                source: "reset-password",
              });
            }}
            href="https://www.fortect.com/support/"
            target="_blank"
            rel="noreferrer"
            className={styles.root_link}
          >
            {t("Contact support")}
          </a>
        </div>
      ) : !isChangePasswordModalVisible && isModalVisible ? (
        <SendCodeModal
          title={t("Check your Email for the Magic Code")}
          subtitle={t("Please check your inbox for an email containing a code to reset your password.")}
          message={t("If you don't see the email, make sure to look in your spam/junk folder.")}
          handleCodeSubmit={handleCodeSubmit}
          setIsModalSendLinkVisible={setIsModalVisible}
          setIsModalNewPasswordVisible={setIsChangePasswordModalVisible}
          isForgotPasswordPage={true}
        />
      ) : (
        <UpdatePasswordModal isForgotPasswordPage={isForgotPasswordPage} />
      )}
    </>
  );
};

export default ResetPasswordForm;
